import React from "react";
import { useEffect, useState } from "react";
import addGroupImage from "../../../../../../assets/Add Group.png";
import toast from "react-hot-toast";
import { useDemography } from "../../createCohort/components/hooks/useProfile";
import { api } from "../../../../../../api/profile-enrollment";
import CustomDropdown from "../../../../../../components/dropdown/CustomDropdown";

type CohortInformationType = {
  setEnableTeams: React.Dispatch<React.SetStateAction<boolean>>;
  enableTeams: boolean;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  setCohortId: React.Dispatch<React.SetStateAction<string | null>>;
  setStatus: React.Dispatch<React.SetStateAction<boolean>>;
  cohortId: string | null;
  status: boolean;
  cohort: any;
};

interface DemographyField {
  fieldType: string;
  fieldName: string;
  id: string;
  fieldKey: string;
  demographyField: {
    fieldType: string;
    fieldName: string;
    id: string;
    fieldKey: string;
  };
}

const STORAGE_KEY = "selected_demographic_fields";

const EditProfileEnrollment: React.FC<CohortInformationType> = ({
  setCurrentStep,
  cohortId,
  cohort,
}) => {
  const [selectedFields, setSelectedFields] = useState<
    { value: string; label: string; fieldKey: string }[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const orgId = JSON.parse(sessionStorage.getItem("user") ?? "{}")?.org?.id;

 
// 1. Load saved fields from sessionStorage or fallback to cohortDemographics
useEffect(() => {
  if (!cohort?.cohortDemographics || initialized) return;

  const saved = sessionStorage.getItem(STORAGE_KEY);

  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      setSelectedFields(parsed);
    } catch (err) {
      console.error('Error parsing sessionStorage data:', err);
    }
  } else {
    const mapped = cohort.cohortDemographics.map((item: any) => ({
      label: item.demographyField.fieldName,
      value: item.demographyField.id,
      fieldKey: item.demographyField.fieldKey,
    }));
    setSelectedFields(mapped);
  }

  setInitialized(true); // prevents rerunning the effect
}, [cohort, initialized]);


// 2. Save to sessionStorage whenever selectedFields change
useEffect(() => {
  if (!initialized) return;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selectedFields));
}, [selectedFields, initialized]);

const fieldOptions =
  cohort?.cohortDemographics?.map((item: any) => ({
    label: item.demographyField.fieldName,
    value: item.demographyField.id,
    fieldKey: item.demographyField.fieldKey,
  })) || [];

  // Load saved fields from sessionStorage when component mounts
  // useEffect(() => {
  //   const savedFields = sessionStorage.getItem(STORAGE_KEY);
  
  //   // If sessionStorage is empty and cohort is loaded, populate selectedFields from API
  //   if (!savedFields && cohort?.cohortDemographics) {
  //     const defaultFields = cohort.cohortDemographics.map((item: any) => ({
  //       label: item.demographyField.fieldName,
  //       value: item.demographyField.id,
  //       fieldKey: item.demographyField.fieldKey,
  //     }));
  //     setSelectedFields(defaultFields);
  //   }
  // }, [cohort]);

  // // Update sessionStorage when selectedFields changes
  // useEffect(() => {
  //   if (selectedFields.length > 0) {
  //     sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selectedFields));
  //   }
  // }, [selectedFields]);

  const validateRequiredFields = () => {
    // Check if name and email fields are selected
    const hasNameField = selectedFields.some(
      (field) =>
        field.fieldKey === "name" || field.label.toLowerCase().includes("name")
    );

    const hasEmailField = selectedFields.some(
      (field) =>
        field.fieldKey === "email" ||
        field.label.toLowerCase().includes("email")
    );

    if (!hasNameField || !hasEmailField) {
      setError("Name and Email fields are required");
      return false;
    }

    setError(null);
    return true;
  };

  const saveCohortDemography = async (): Promise<void> => {
    try {
      // Validate required fields before proceeding
      if (!validateRequiredFields()) {
        return;
      }

      toast.loading("Loading...");

      const payload = {
        fields: selectedFields.map((item, index) => ({
          demographyFormFieldId: item.value,
          sort: index + 1,
        })),
      };
      const response: any = await api.addDemography({
        orgId,
        cohortId,
        payload,
      });
      toast.remove();
      toast.success(response?.message);
      setCurrentStep(3);
    } catch (error: any) {
      toast.remove();
      toast.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const handleFieldChange = (value: any) => {
    setSelectedFields([...value]);
    setError(null); // Clear error when selection changes
  };

 

  return (
    <>
      <section className="border flex justify-between pr-0 px-7 border-slate-400 rounded-lg mx-3 mt-8">
        <div className="w-[40%] py-7 flex items-center justify-center">
          <img
            src={addGroupImage || "/placeholder.svg"}
            className="w-[260px] h-[260px]"
            alt="add group"
          />
        </div>
        <section className="w-[52%]">
          <div className="rounded-e-lg bg-white p-5 px-8 pb-12">
            <h5 className="text-xl font-semibold">Update Profile Enrollment</h5>
            <p className="text-xs font-semibold border-b border-gray-300 pb-2 mt-1 mb-3">
              Select Demography Fields
            </p>

            <div className="space-y-4">
              <div>
                <div className="relative w-full">
                  <CustomDropdown
                    options={fieldOptions}
                    value={selectedFields}
                    onChange={handleFieldChange}
                    multiSelect
                    placeholder="Choose fields"
                    onBlur={null}
                    name="fields"
                    className="bg-primaryGray select-custom custom-dropdown"
                    width="w-full"
                  />
                </div>
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                <p className="text-xs text-gray-500 mt-2">
                  <span className="font-semibold">Note:</span> Name and Email
                  fields are required
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={saveCohortDemography}
                className="btn bg-primary-blue flex-1 text-center"
              >
                Add demography
              </button>
            </div>
          </div>
        </section>
      </section>
    </>
  );
};

export default EditProfileEnrollment;
